"use client"

import * as React from "react"
import { Command } from "cmdk"
import { Check, ChevronsUpDown } from "lucide-react"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type ComboboxContextValue = {
  value: string
  onValueChange: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  items: readonly string[]
  itemToLabel?: (item: string) => string
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null)

function useCombobox() {
  const context = React.useContext(ComboboxContext)
  if (!context) {
    throw new Error("useCombobox must be used within <Combobox>")
  }
  return context
}

interface ComboboxProps {
  items: readonly string[]
  value?: string
  onValueChange?: (value: string) => void
  defaultValue?: string
  itemToLabel?: (item: string) => string
  children: React.ReactNode
  className?: string
}

function Combobox({
  items,
  value: controlledValue,
  onValueChange,
  defaultValue,
  itemToLabel,
  children,
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "")
  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue

  const handleValueChange = (next: string) => {
    if (!isControlled) {
      setInternalValue(next)
    }
    onValueChange?.(next)
    setOpen(false)
  }

  return (
    <ComboboxContext.Provider
      value={{
        value,
        onValueChange: handleValueChange,
        open,
        setOpen,
        items,
        itemToLabel,
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cn("relative w-full", className)}>{children}</div>
      </Popover>
    </ComboboxContext.Provider>
  )
}

function ComboboxInput({
  className,
  placeholder,
  ...props
}: React.ComponentProps<"button"> & { placeholder?: string }) {
  const { value, itemToLabel, open, setOpen } = useCombobox()
  const displayValue = value ? (itemToLabel ? itemToLabel(value) : value) : ""

  return (
    <PopoverTrigger asChild>
      <button
        type="button"
        data-slot="combobox-input"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !displayValue && "text-muted-foreground",
          className
        )}
        {...props}
      >
        <span className="truncate">{displayValue || placeholder}</span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
  )
}

function ComboboxContent({
  className,
  searchPlaceholder,
  children,
  ...props
}: React.ComponentProps<typeof PopoverContent> & {
  searchPlaceholder?: string
}) {
  return (
    <PopoverContent
      align="start"
      className={cn("w-[--radix-popover-trigger-width] p-0", className)}
      {...props}
    >
      <Command>
        <Command.Input
          placeholder={searchPlaceholder}
          className="flex h-10 w-full rounded-t-md border-b border-border bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
        />
        <Command.List className="max-h-64 overflow-y-auto p-1">
          {children}
        </Command.List>
      </Command>
    </PopoverContent>
  )
}

function ComboboxEmpty({
  className,
  ...props
}: React.ComponentProps<typeof Command.Empty>) {
  return (
    <Command.Empty
      data-slot="combobox-empty"
      className={cn("py-6 text-center text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function ComboboxList({
  children,
}: {
  children: (item: string) => React.ReactNode
}) {
  const { items } = useCombobox()

  return <>{items.map((item) => children(item))}</>
}

function ComboboxItem({
  value,
  onSelect,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof Command.Item>, "value"> & {
  value: string
}) {
  const { value: selectedValue, onValueChange, itemToLabel } = useCombobox()
  const isSelected = value === selectedValue
  const label = itemToLabel ? itemToLabel(value) : value

  return (
    <Command.Item
      data-slot="combobox-item"
      value={label}
      onSelect={() => {
        onValueChange(value)
        onSelect?.(value)
      }}
      className={cn(
        "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-2 text-sm outline-none",
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      <Check
        className={cn(
          "ml-auto size-4",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
    </Command.Item>
  )
}

export {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
}
